import { forwardRef } from 'react';

import logo from '../../../assets/images/logo.png';
import { CopySVG } from '../../utils/svgs';
import { fmtNum } from '../child-agent/utils';
import type { IReceiptModel } from './model';
import Seal from './Seal';

const CheckMark = () => (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M4 10.5 8 14.5 16 5.5'
			stroke='currentColor'
			strokeWidth='2.2'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

interface ICertificateProps {
	model: IReceiptModel;
	/** Inlined (data-URL) result-sheet images, index-aligned to model.documentUrls.
	 * `null` → fetch failed; render a placeholder so the export never breaks. */
	sheetImages: (string | null)[];
	qrDataUrl: string | null;
	onCopyReference?: () => void;
	referenceCopied?: boolean;
	onThumbClick?: (index: number) => void;
}

/** The printable certificate. This whole subtree is what gets captured to PDF/PNG,
 * so it is theme-pinned to light via `data-doc-theme="light"` and contains no
 * app-chrome. Interactive-only affordances carry `data-html2canvas-ignore`. */
const Certificate = forwardRef<HTMLDivElement, ICertificateProps>(
	({ model, sheetImages, qrDataUrl, onCopyReference, referenceCopied, onThumbClick }, ref) => {
		const { kpis } = model;

		return (
			<div
				className='completion-receipt__paper'
				data-doc-theme='light'
				ref={ref}
				role='document'
				aria-label={`Certificate confirming ${model.agentName} submitted the ${model.levelLabel} result for ${
					model.electionName
				}. Reference ${model.referenceId}.`}
			>
				<p
					className='completion-receipt__sr-summary'
					data-html2canvas-ignore
				>
					Certificate of result submission. Agent {model.agentName} completed and submitted the {model.levelLabel} result for{' '}
					{model.electionName} on {model.submittedAt}. Reference {model.referenceId}. Verification code{' '}
					{model.verification.humanCode}.
				</p>

				{/* Header: brand + seal */}
				<header className='completion-receipt__header'>
					<div className='completion-receipt__brand'>
						<span className='completion-receipt__eyebrow'>iElect · Official Record</span>
						<img
							className='completion-receipt__logo'
							src={logo}
							alt='iElect'
						/>
					</div>
					<div className='completion-receipt__seal'>
						<Seal
							size={104}
							verified
						/>
					</div>
				</header>

				<hr className='completion-receipt__rule' />
				<h2 className='completion-receipt__headline'>Certificate of Result Submission</h2>
				<hr className='completion-receipt__rule' />

				<div className='completion-receipt__status completion-receipt__status--success'>
					<span className='completion-receipt__status-icon'>
						<CheckMark />
					</span>
					Completed · {model.levelLabel} ({model.resultType}) result recorded
				</div>

				{/* Identity */}
				<section className='completion-receipt__identity'>
					<div className='completion-receipt__id-grid'>
						{model.identity.map((f) => (
							<div
								className='completion-receipt__id-cell'
								key={f.label}
							>
								<span className='completion-receipt__id-label'>{f.label}</span>
								<span className={`completion-receipt__id-value ${f.mono ? 'is-mono' : ''}`}>{f.value}</span>
							</div>
						))}
						<div className='completion-receipt__id-cell completion-receipt__id-cell--wide'>
							<span className='completion-receipt__id-label'>Election</span>
							<span className='completion-receipt__id-value'>{model.electionName}</span>
						</div>
						<div className='completion-receipt__id-cell'>
							<span className='completion-receipt__id-label'>Presiding officer</span>
							<span className='completion-receipt__id-value'>{model.presidingOfficer}</span>
						</div>
					</div>
				</section>

				{/* Reference + verification */}
				<section className='completion-receipt__ref'>
					<div className='completion-receipt__ref-main'>
						<span className='completion-receipt__id-label'>Reference ID</span>
						<div className='completion-receipt__refchip'>
							<span>{model.referenceId || '—'}</span>
							{onCopyReference ? (
								<button
									type='button'
									className='completion-receipt__copy'
									onClick={onCopyReference}
									aria-label='Copy reference ID'
									data-html2canvas-ignore
								>
									{referenceCopied ? <CheckMark /> : <CopySVG />}
								</button>
							) : null}
						</div>
						<span className='completion-receipt__verifycode'>
							Verify code · <strong>{model.verification.humanCode}</strong>
						</span>
						<span className='completion-receipt__verifyhint'>Scan or quote this reference to verify authenticity.</span>
					</div>
					<div className='completion-receipt__qr'>
						{qrDataUrl ? (
							<img
								src={qrDataUrl}
								alt='Verification QR code'
							/>
						) : (
							<div className='completion-receipt__qr-fallback'>{model.verification.humanCode}</div>
						)}
					</div>
				</section>

				{/* Recorded figures + turnout — deferred until the read-store result projects */}
				{!model.figuresAvailable ? (
					<section className='completion-receipt__pending'>
						<span className='completion-receipt__section-title'>Recorded figures</span>
						<p>
							This certificate confirms your submission was recorded. The detailed recorded figures are still being processed and
							will appear here once available — re-open this certificate shortly to include them.
						</p>
					</section>
				) : (
					<>
						<section className='completion-receipt__figures'>
							<span className='completion-receipt__section-title'>Recorded figures</span>
							<div className='completion-receipt__fig-layout'>
								<div className='completion-receipt__fig-grid'>
									{model.figures.map((f) => (
										<div
											className='completion-receipt__fig-cell'
											key={f.label}
										>
											<span className='completion-receipt__fig-label'>{f.label}</span>
											<span className='completion-receipt__fig-value'>{fmtNum(f.value)}</span>
										</div>
									))}
									<div className='completion-receipt__fig-cell completion-receipt__fig-cell--valid'>
										<span className='completion-receipt__fig-label'>Valid votes</span>
										<span className='completion-receipt__fig-value'>{fmtNum(model.validVotes)}</span>
									</div>
								</div>
								<div className='completion-receipt__ring'>
									<Ring value={kpis.turnout} />
									<span className='completion-receipt__ring-caption'>Voter turnout</span>
								</div>
							</div>
						</section>

						{/* Party votes */}
						{model.parties.length ? (
							<section className='completion-receipt__parties'>
								<div className='completion-receipt__parties-head'>
									<span className='completion-receipt__section-title'>Party votes</span>
									{model.leader ? (
										<span
											className='completion-receipt__leader'
											style={{ background: model.leader.color.text, color: model.leader.color.bg }}
										>
											Leading · {model.leader.name} · {fmtNum(model.leader.votes)}
										</span>
									) : null}
								</div>
								<ul className='completion-receipt__bars'>
									{model.parties.map((p) => (
										<li
											className='completion-receipt__bar-row'
											key={p.name}
										>
											<span
												className='completion-receipt__bar-tag'
												style={{ background: p.color.bg, color: p.color.text }}
											>
												{p.name}
											</span>
											<span className='completion-receipt__bar-track'>
												<span
													className='completion-receipt__bar-fill'
													style={{ width: `${p.pct}%`, background: p.color.bg }}
												/>
											</span>
											<span className='completion-receipt__bar-votes'>{fmtNum(p.votes)}</span>
										</li>
									))}
								</ul>
								{kpis.mismatch ? (
									<p className='completion-receipt__mismatch'>
										Party votes total {fmtNum(kpis.tallied)}, but recorded valid votes are {fmtNum(model.validVotes)}.
									</p>
								) : null}
							</section>
						) : null}

						{/* Result sheets */}
						{model.documentUrls.length ? (
							<section className='completion-receipt__sheets'>
								<span className='completion-receipt__section-title'>Result sheet{model.documentUrls.length > 1 ? 's' : ''}</span>
								<div className='completion-receipt__thumbs'>
									{model.documentUrls.map((url, i) => {
										const img = sheetImages[i];
										return img ? (
											<button
												type='button'
												className='completion-receipt__thumb'
												key={url}
												onClick={() => onThumbClick?.(i)}
												aria-label={`Open result sheet ${i + 1}`}
											>
												<img
													src={img}
													alt={`Result sheet ${i + 1}`}
												/>
												<span
													className='completion-receipt__thumb-zoom'
													data-html2canvas-ignore
												>
													View
												</span>
											</button>
										) : (
											<div
												className='completion-receipt__thumb completion-receipt__thumb--missing'
												key={url}
											>
												Sheet {i + 1}
												<small>image unavailable</small>
											</div>
										);
									})}
								</div>
							</section>
						) : null}
					</>
				)}

				{/* Timestamps */}
				<section className='completion-receipt__timestamps'>
					<div className='completion-receipt__time-cell'>
						<span className='completion-receipt__id-label'>Submitted</span>
						<span className='completion-receipt__id-value'>{model.submittedAt}</span>
					</div>
					{model.approvedAt ? (
						<div className='completion-receipt__time-cell'>
							<span className='completion-receipt__id-label'>Approved</span>
							<span className='completion-receipt__id-value'>{model.approvedAt}</span>
						</div>
					) : null}
					<div className='completion-receipt__time-cell'>
						<span className='completion-receipt__id-label'>Certificate issued</span>
						<span className='completion-receipt__id-value'>{model.generatedAt}</span>
					</div>
				</section>

				{/* Footer */}
				<footer className='completion-receipt__footer'>
					<div className='completion-receipt__statement'>
						<p>
							This certifies that <strong>{model.agentName}</strong> recorded and submitted the {model.levelLabel} result above
							for <strong>{model.electionName}</strong> on the iElect Election Results Management System.
						</p>
						<p className='completion-receipt__hash'>
							Integrity {model.verification.hash} · Any change to these figures invalidates this certificate.
						</p>
					</div>
					<div className='completion-receipt__sign'>
						<Seal
							size={56}
							verified
						/>
						<span className='completion-receipt__sign-line'>Issued by iElect</span>
					</div>
				</footer>
			</div>
		);
	}
);

Certificate.displayName = 'Certificate';

// ── Turnout ring (self-contained; mirrors the View-Result ProgressRing maths) ──
const Ring = ({ value }: { value: number | null }) => {
	const pct = value == null ? 0 : Math.min(Math.max(value * 100, 0), 100);
	const radius = 34;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (pct / 100) * circumference;
	return (
		<div className='completion-receipt__ring-svg'>
			<svg viewBox='0 0 80 80'>
				<circle
					className='completion-receipt__ring-track'
					cx='40'
					cy='40'
					r={radius}
					fill='none'
					strokeWidth='7'
				/>
				<circle
					className='completion-receipt__ring-value'
					cx='40'
					cy='40'
					r={radius}
					fill='none'
					strokeWidth='7'
					strokeLinecap='round'
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					transform='rotate(-90 40 40)'
				/>
			</svg>
			<span className='completion-receipt__ring-label'>{value == null ? '—' : `${Math.round(pct)}%`}</span>
		</div>
	);
};

export default Certificate;
